import { Box, Skeleton } from '@mui/material'
import { cn } from '@/lib/utils'

export interface LoadingProps {
  /** Quantas linhas de esqueleto. Aproxime do conteúdo que vai chegar. */
  rows?: number | undefined
  /** Nome do que está carregando, lido por leitor de tela. */
  label?: string | undefined
  className?: string | undefined
  /**
   * Forma do esqueleto. `lines` é o padrão; `list` põe avatar e duas linhas
   * por item; `cards` desenha blocos numa grade.
   */
  shape?: 'lines' | 'list' | 'cards' | undefined
}

/** As linhas não reordenam nem somem: a chave é a própria posição. */
function rowKeys(rows: number): string[] {
  return Array.from({ length: rows }, (_, index) => `linha-${index}`)
}

/**
 * Espera.
 *
 * Esqueleto em vez de roda girando: a tela já mostra a forma do que vem, e
 * o salto na hora que o dado chega é menor. O brilho que passa é a onda do
 * tema (ui/style/motion.css), que a sala desliga.
 */
export function Loading({
  rows = 3,
  label = 'Carregando',
  className,
  shape = 'lines',
}: LoadingProps) {
  const keys = rowKeys(rows)
  return (
    <Box
      data-slot="loading"
      role="status"
      aria-busy="true"
      aria-label={label}
      className={cn(
        shape === 'cards' && 'grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3',
        shape === 'list' && 'flex flex-col gap-4 p-4',
        shape === 'lines' && 'flex flex-col gap-2 p-4',
        className,
      )}
    >
      {shape === 'lines' &&
        keys.map((key, index) => (
          <Skeleton
            key={key}
            data-slot="skeleton"
            variant="rounded"
            height={16}
            width={index === rows - 1 && rows > 1 ? '66%' : '100%'}
          />
        ))}
      {shape === 'list' &&
        keys.map((key, index) => (
          <Box key={key} data-slot="skeleton" className="flex flex-row items-center gap-3">
            <Skeleton variant="circular" width={40} height={40} className="flex-none" />
            <Box className="flex min-w-0 flex-1 flex-col gap-1.5">
              <Skeleton variant="rounded" height={14} width={index % 2 === 0 ? '55%' : '40%'} />
              <Skeleton variant="rounded" height={12} width={index % 2 === 0 ? '80%' : '70%'} />
            </Box>
          </Box>
        ))}
      {shape === 'cards' &&
        keys.map((key) => (
          <Skeleton key={key} data-slot="skeleton" variant="rounded" height={112} />
        ))}
    </Box>
  )
}
